import { Navigate, Outlet, useLocation } from 'react-router-dom';
import type { UserRole } from '@klaro7/shared-types';
import { Spinner } from '@klaro7/ui';

import {
  needsPhoneVerification,
  PHONE_VERIFICATION_PATH,
} from '@/features/phone-verification/utils/phoneGate';
import {
  needsEmailVerification,
  EMAIL_VERIFICATION_PATH,
} from '@/features/email-verification/utils/emailGate';

import { useAuth } from '../hooks/useAuth';
import { getDefaultRedirect } from '../context/AuthContext';

interface ProtectedRouteProps {
  allowedRoles?: UserRole[];
  modalLogin?: boolean;
}

export function ProtectedRoute({ allowedRoles, modalLogin }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, hasRole, user, openLoginModal } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    if (modalLogin) {
      openLoginModal(location.pathname);
      return <Navigate to="/" replace />;
    }
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (user?.mustChangePassword && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }

  // Doppio opt-in (2026-08-27): email non confermata viene PRIMA del telefono
  // — l'OTP telefono non è nemmeno richiedibile server-side finché il link
  // email non è stato cliccato (vedi phone-verification.service.ts).
  if (needsEmailVerification(user) && location.pathname !== EMAIL_VERIFICATION_PATH) {
    return (
      <Navigate to={EMAIL_VERIFICATION_PATH} state={{ redirectTo: location.pathname }} replace />
    );
  }

  // Telefono non verificato = nessuna superficie autenticata. Viene dopo il
  // cambio password forzato (quello ha la precedenza) e prima dei ruoli.
  if (needsPhoneVerification(user) && location.pathname !== PHONE_VERIFICATION_PATH) {
    return (
      <Navigate to={PHONE_VERIFICATION_PATH} state={{ redirectTo: location.pathname }} replace />
    );
  }

  if (allowedRoles && !hasRole(allowedRoles)) {
    return <Navigate to={getDefaultRedirect(user!.role)} replace />;
  }

  return <Outlet />;
}
